import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useParams, useNavigate } from 'react-router-dom';
import { PAGE_ROUTE, API_URL } from '../../shared/constant';
import { Form, Modal, Button, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import LoaderComponent from '../../shared/loader';
import axios from 'axios';

const MAX_FAQS_SYNC = 500;

const normalizeFaqsList = (data) => {
    if (!data) return [];
    let list = [];
    if (Array.isArray(data)) list = data;
    else if (Array.isArray(data.faqs)) list = data.faqs;
    else if (Array.isArray(data.data)) list = data.data;
    return list.map((item, i) => ({
        index: item.index !== undefined && item.index !== null ? Number(item.index) : i,
        question: item.question || '',
        answer: item.answer || ''
    }));
};

const PostsFaqsComponent = () => {
    const { postId } = useParams();
    const navigate = useNavigate();
    const pid = parseInt(postId, 10);

    const [postTitle, setPostTitle] = useState('');
    const [faqs, setFaqs] = useState([]);
    const [isPageLoading, setPageLoading] = useState(false);
    const [isSaving, setSaving] = useState(false);
    const [modalShow, setModalShow] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [editIndex, setEditIndex] = useState(null);
    const [formQuestion, setFormQuestion] = useState('');
    const [formAnswer, setFormAnswer] = useState('');
    const [syncJson, setSyncJson] = useState('');
    const [syncModalOpen, setSyncModalOpen] = useState(false);

    const loadPost = useCallback(() => {
        if (!pid || Number.isNaN(pid)) return;
        axios.post(API_URL.ADMIN_POSTS_GET, { id: pid }).then((res) => {
            if (res.data.status === 1 && res.data.data) {
                setPostTitle(res.data.data.title || `Post #${pid}`);
            }
        }).catch(() => {});
    }, [pid]);

    const loadFaqs = useCallback(() => {
        if (!pid || Number.isNaN(pid)) return;
        setPageLoading(true);
        axios.post(API_URL.ADMIN_POSTS_FAQS_LIST, { post_id: pid }).then((res) => {
            setPageLoading(false);
            if (res.data.status === 1) {
                setFaqs(normalizeFaqsList(res.data.data));
            } else {
                toast.error(res.data.data || 'Failed to load FAQs');
            }
        }).catch(() => {
            setPageLoading(false);
            toast.error('Failed to load FAQs');
        });
    }, [pid]);

    useEffect(() => {
        if (!pid || Number.isNaN(pid)) {
            toast.error('Invalid post');
            navigate(PAGE_ROUTE.ADMIN_POSTS_LIST);
            return;
        }
        loadPost();
        loadFaqs();
    }, [pid, loadPost, loadFaqs, navigate]);

    const openAdd = () => {
        setModalMode('add');
        setEditIndex(null);
        setFormQuestion('');
        setFormAnswer('');
        setModalShow(true);
    };

    const openEdit = (row) => {
        setModalMode('edit');
        setEditIndex(row.index);
        setSaving(true);
        axios.post(API_URL.ADMIN_POSTS_FAQS_SHOW, { post_id: pid, index: row.index }).then((res) => {
            setSaving(false);
            if (res.data.status === 1) {
                const d = res.data.data || {};
                setFormQuestion(d.question || row.question || '');
                setFormAnswer(d.answer || row.answer || '');
                setModalShow(true);
            } else {
                setFormQuestion(row.question);
                setFormAnswer(row.answer);
                setModalShow(true);
            }
        }).catch(() => {
            setSaving(false);
            setFormQuestion(row.question);
            setFormAnswer(row.answer);
            setModalShow(true);
        });
    };

    const saveModal = () => {
        const q = formQuestion.trim();
        const a = formAnswer.trim();
        if (!q) {
            toast.error('Question is required');
            return;
        }
        if (!a) {
            toast.error('Answer is required');
            return;
        }
        setSaving(true);
        if (modalMode === 'add') {
            axios.post(API_URL.ADMIN_POSTS_FAQS_STORE, { post_id: pid, question: q, answer: a }).then((res) => {
                setSaving(false);
                setModalShow(false);
                if (res.data.status === 1) {
                    toast.success('FAQ added');
                    loadFaqs();
                } else {
                    toast.error(res.data.data || 'Failed to add FAQ');
                }
            }).catch(() => {
                setSaving(false);
                toast.error('Failed to add FAQ');
            });
        } else {
            axios.post(API_URL.ADMIN_POSTS_FAQS_UPDATE, {
                post_id: pid,
                index: editIndex,
                question: q,
                answer: a
            }).then((res) => {
                setSaving(false);
                setModalShow(false);
                if (res.data.status === 1) {
                    toast.success('FAQ updated');
                    loadFaqs();
                } else {
                    toast.error(res.data.data || 'Failed to update FAQ');
                }
            }).catch(() => {
                setSaving(false);
                toast.error('Failed to update FAQ');
            });
        }
    };

    const onDelete = (row) => {
        if (!window.confirm('Delete this FAQ? Later indices will shift.')) return;
        setSaving(true);
        axios.post(API_URL.ADMIN_POSTS_FAQS_DELETE, { post_id: pid, index: row.index }).then((res) => {
            setSaving(false);
            if (res.data.status === 1) {
                toast.success('FAQ deleted');
                loadFaqs();
            } else {
                toast.error(res.data.data || 'Failed to delete');
            }
        }).catch(() => {
            setSaving(false);
            toast.error('Failed to delete');
        });
    };

    const clearAllFaqs = () => {
        if (!window.confirm('Remove all FAQs for this post?')) return;
        setSaving(true);
        axios.post(API_URL.ADMIN_POSTS_FAQS_SYNC, { post_id: pid, faqs: [] }).then((res) => {
            setSaving(false);
            if (res.data.status === 1) {
                toast.success('All FAQs cleared');
                loadFaqs();
            } else {
                toast.error(res.data.data || 'Failed to clear');
            }
        }).catch(() => {
            setSaving(false);
            toast.error('Failed to clear');
        });
    };

    const applySync = () => {
        let parsed;
        try {
            parsed = JSON.parse(syncJson.trim() || '[]');
        } catch {
            toast.error('Invalid JSON');
            return;
        }
        if (!Array.isArray(parsed)) {
            toast.error('Root must be a JSON array');
            return;
        }
        const faqsPayload = parsed.map((item) => ({
            question: String(item.question != null ? item.question : '').trim(),
            answer: String(item.answer != null ? item.answer : '').trim()
        })).filter((x) => x.question || x.answer);
        if (faqsPayload.length > MAX_FAQS_SYNC) {
            toast.error(`Maximum ${MAX_FAQS_SYNC} FAQs allowed`);
            return;
        }
        if (!window.confirm(`Replace entire FAQ list with ${faqsPayload.length} item(s)?`)) return;
        setSaving(true);
        axios.post(API_URL.ADMIN_POSTS_FAQS_SYNC, { post_id: pid, faqs: faqsPayload }).then((res) => {
            setSaving(false);
            setSyncModalOpen(false);
            setSyncJson('');
            if (res.data.status === 1) {
                toast.success('FAQs synced');
                loadFaqs();
            } else {
                toast.error(res.data.data || 'Sync failed');
            }
        }).catch(() => {
            setSaving(false);
            toast.error('Sync failed');
        });
    };

    const exportJson = () => {
        const payload = faqs.map(({ question, answer }) => ({ question, answer }));
        setSyncJson(JSON.stringify(payload, null, 2));
        setSyncModalOpen(true);
    };

    if (!pid || Number.isNaN(pid)) return null;

    return (
        <div className="card">
            <div className="card-body">
                <h4 className="card-title mb-3">
                    Post FAQs
                    <span className="text-muted ms-2" style={{ fontSize: '1rem' }}>
                        {postTitle ? `— ${postTitle}` : `(ID ${pid})`}
                    </span>
                </h4>
                <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                    <NavLink to={PAGE_ROUTE.ADMIN_POSTS_EDIT + pid} className="btn btn-outline-secondary btn-sm">
                        ← Edit post
                    </NavLink>
                    <NavLink to={PAGE_ROUTE.ADMIN_POSTS_LIST} className="btn btn-outline-secondary btn-sm">
                        Posts list
                    </NavLink>
                    <Button variant="success" size="sm" onClick={openAdd}>
                        Add FAQ
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={exportJson}>
                        Bulk sync (JSON)
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={clearAllFaqs} disabled={!faqs.length}>
                        Clear all
                    </Button>
                </div>
                <Alert variant="info" className="py-2 small">
                    APIs: list, show, store, update, delete, sync (max {MAX_FAQS_SYNC} items). Deleting shifts later indices.
                </Alert>
                {isPageLoading && <LoaderComponent height={80} width={80} />}
                {!isPageLoading && (
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="thead">
                                <tr>
                                    <th style={{ width: '70px' }}>#</th>
                                    <th>Question</th>
                                    <th>Answer</th>
                                    <th style={{ width: '180px' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {faqs.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="text-muted text-center py-4">
                                            No FAQs yet. Add one or use bulk sync.
                                        </td>
                                    </tr>
                                )}
                                {faqs.map((row) => (
                                    <tr key={`${row.index}-${row.question?.slice(0, 20)}`}>
                                        <td>{row.index}</td>
                                        <td title={row.question}>
                                            {row.question && row.question.length > 80
                                                ? `${row.question.slice(0, 80)}…`
                                                : row.question}
                                        </td>
                                        <td title={row.answer}>
                                            {row.answer && row.answer.length > 120
                                                ? `${row.answer.slice(0, 120)}…`
                                                : row.answer}
                                        </td>
                                        <td>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => openEdit(row)}
                                            >
                                                Edit
                                            </Button>
                                            <Button variant="outline-danger" size="sm" onClick={() => onDelete(row)}>
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <Modal show={modalShow} onHide={() => setModalShow(false)} backdrop="static" size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{modalMode === 'add' ? 'Add FAQ' : `Edit FAQ (index ${editIndex})`}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Question</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={formQuestion}
                                onChange={(e) => setFormQuestion(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Answer</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                value={formAnswer}
                                onChange={(e) => setFormAnswer(e.target.value)}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setModalShow(false)} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={saveModal} disabled={isSaving}>
                            {isSaving ? '…' : modalMode === 'add' ? 'Add' : 'Update'}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={syncModalOpen} onHide={() => setSyncModalOpen(false)} backdrop="static" size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Replace all FAQs (sync)</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="small text-muted">
                            Paste a JSON array: <code>[{`{"question":"...","answer":"..."}`}, …]</code>
                            (max {MAX_FAQS_SYNC}). Empty array clears all.
                        </p>
                        <Form.Control
                            as="textarea"
                            rows={14}
                            value={syncJson}
                            onChange={(e) => setSyncJson(e.target.value)}
                            className="font-monospace small"
                            placeholder='[{"question":"Q1","answer":"A1"}]'
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setSyncModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={applySync} disabled={isSaving}>
                            {isSaving ? '…' : 'Apply sync'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default PostsFaqsComponent;
